import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  LayoutDashboard,
  Users,
  Clock,
  Building2,
  Timer,
  Settings,
  Palmtree,
  CalendarCheck,
  ClockAlert,
  FileText,
  ReceiptText,
  BookUser,
  IndianRupee,
  BarChart2,
  Megaphone,
  Receipt,
  Inbox,
  Sun,
  Moon,
  LogOut,
  ListChecks,
  KeyRound,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useLogout } from '@/hooks/useAuth';
import { useUiStore } from '@/stores/ui.store';
import { cn } from '@/lib/utils';
import { SHORTCUT_LABEL } from '@/lib/platform';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';

// ── Nav item definition ──────────────────────────────────────────────────────

type UserRole = 'SUPER_ADMIN' | 'ORG_ADMIN' | 'HR' | 'MANAGER' | 'EMPLOYEE';

interface PaletteItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
  keywords?: string;
  allow?: UserRole[];
}

const NAV_ITEMS: PaletteItem[] = [
  // Core
  { group: 'Core HR', label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  {
    group: 'Core HR',
    label: 'Employees',
    to: '/employees',
    icon: Users,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
  },
  { group: 'Core HR', label: 'Attendance', to: '/attendance', icon: Clock },
  {
    group: 'Core HR',
    label: 'Departments',
    to: '/departments',
    icon: Building2,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  {
    group: 'Core HR',
    label: 'Shifts',
    to: '/shifts',
    icon: Timer,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  {
    group: 'Core HR',
    label: 'Reimbursements',
    to: '/reimbursements',
    icon: Receipt,
    keywords: 'claims expenses bills fuel travel',
  },
  {
    group: 'Core HR',
    label: 'Approval Inbox',
    to: '/approval-inbox',
    icon: Inbox,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
  },

  // Leaves
  {
    group: 'Leaves',
    label: 'Leave Management',
    to: '/leaves',
    icon: ListChecks,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
  },
  { group: 'Leaves', label: 'My Leaves', to: '/my-leaves', icon: CalendarCheck },
  { group: 'Leaves', label: 'Holiday Calendar', to: '/holidays', icon: Palmtree },
  { group: 'Leaves', label: 'Regularisation', to: '/regularisation', icon: ClockAlert },

  // Payroll
  {
    group: 'Payroll',
    label: 'Payroll Runs',
    to: '/payroll',
    icon: ReceiptText,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  {
    group: 'Payroll',
    label: 'Salary Structure',
    to: '/salary-structure',
    icon: BarChart2,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  { group: 'Payroll', label: 'My Payslips', to: '/my-payslips', icon: IndianRupee },
  { group: 'Payroll', label: 'My Letters', to: '/my-letters', icon: FileText },

  // Company
  { group: 'Company', label: 'Announcements', to: '/announcements', icon: Megaphone },
  { group: 'Company', label: 'Employee Directory', to: '/directory', icon: BookUser },

  // Reports & Support
  {
    group: 'Reports',
    label: 'Reports',
    to: '/reports',
    icon: FileText,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'],
  },
  // Hidden for the field launch: Office Locations, Organisation Chart, HR Policies

  // Settings
  { group: 'Account', label: 'My Profile / Settings', to: '/settings', icon: Settings },
];

const QUICK_ITEMS: PaletteItem[] = [
  { group: 'Quick', label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { group: 'Quick', label: 'My Leaves', to: '/my-leaves', icon: CalendarCheck },
  { group: 'Quick', label: 'Attendance', to: '/attendance', icon: Clock },
  { group: 'Quick', label: 'My Payslips', to: '/my-payslips', icon: IndianRupee },
  { group: 'Quick', label: 'Reimbursements', to: '/reimbursements', icon: Receipt },
  {
    group: 'Quick',
    label: 'Approval Inbox',
    to: '/approval-inbox',
    icon: Inbox,
    allow: ['SUPER_ADMIN', 'ORG_ADMIN', 'HR', 'MANAGER'],
  },
  { group: 'Quick', label: 'Announcements', to: '/announcements', icon: Megaphone },
];

// ── Component ────────────────────────────────────────────────────────────────

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useUiStore();
  const user = useAuthStore((s) => s.user);
  const logoutMutation = useLogout();
  const role = user?.role;

  const toggle = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [toggle]);

  function run(fn: () => void) {
    setOpen(false);
    fn();
  }

  function canSee(item: PaletteItem) {
    return !item.allow || (role != null && item.allow.includes(role));
  }

  const visibleNav = NAV_ITEMS.filter(canSee);
  const visibleQuick = QUICK_ITEMS.filter(canSee);

  const groups = [...new Set(visibleNav.map((i) => i.group))];

  const nextTheme = theme === 'light' ? 'dark' : 'light';
  const themeIcon = theme === 'dark' ? Moon : Sun;
  const ThemeIcon = themeIcon;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        {/* Backdrop */}
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />

        {/* Panel */}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-[15%] z-50 w-full max-w-xl -translate-x-1/2',
            'bg-background overflow-hidden rounded-xl border shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[14%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[14%]',
            'duration-150',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>

          <Command>
            <CommandInput placeholder="Search pages, actions…" autoFocus />

            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>

              {/* Quick Actions — shown when no query typed */}
              <CommandGroup heading="Quick Access">
                {visibleQuick.map((item) => (
                  <CommandItem
                    key={item.to}
                    value={item.label}
                    onSelect={() => {
                      run(() => {
                        void navigate(item.to);
                      });
                    }}
                  >
                    <item.icon className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>

              <CommandSeparator />

              {/* All nav pages grouped */}
              {groups.map((group) => {
                const items = visibleNav.filter((i) => i.group === group);
                return (
                  <CommandGroup key={group} heading={group}>
                    {items.map((item) => (
                      <CommandItem
                        key={item.to}
                        value={`${item.label} ${item.group} ${item.keywords ?? ''}`}
                        onSelect={() => {
                          run(() => {
                            void navigate(item.to);
                          });
                        }}
                      >
                        <item.icon className="text-muted-foreground h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                        <CommandShortcut className="text-[11px] opacity-50">
                          {item.group}
                        </CommandShortcut>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}

              <CommandSeparator />

              {/* System actions */}
              <CommandGroup heading="Actions">
                <CommandItem
                  value={`theme toggle ${nextTheme}`}
                  onSelect={() => {
                    run(() => {
                      setTheme(nextTheme);
                    });
                  }}
                >
                  <ThemeIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                  <span>
                    Switch to {nextTheme.charAt(0).toUpperCase() + nextTheme.slice(1)} mode
                  </span>
                </CommandItem>
                <CommandItem
                  value="settings profile"
                  onSelect={() => {
                    run(() => {
                      void navigate('/settings');
                    });
                  }}
                >
                  <Settings className="text-muted-foreground h-4 w-4 shrink-0" />
                  <span>My Profile & Settings</span>
                </CommandItem>
                <CommandItem
                  value="change password security"
                  onSelect={() => {
                    run(() => {
                      void navigate('/change-password');
                    });
                  }}
                >
                  <KeyRound className="text-muted-foreground h-4 w-4 shrink-0" />
                  <span>Change password</span>
                </CommandItem>
                <CommandItem
                  value="logout sign out"
                  onSelect={() => {
                    run(() => {
                      logoutMutation.mutate();
                    });
                  }}
                  className="text-destructive aria-selected:text-destructive"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span>Log out</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>

            {/* Footer hint */}
            <div className="text-muted-foreground flex items-center gap-3 border-t px-3 py-2 text-[11px]">
              <span>
                <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-[10px]">↑↓</kbd>{' '}
                navigate
              </span>
              <span>
                <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-[10px]">↵</kbd> select
              </span>
              <span>
                <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-[10px]">esc</kbd>{' '}
                close
              </span>
              <span className="ml-auto">
                <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-[10px]">
                  {SHORTCUT_LABEL}
                </kbd>{' '}
                to open
              </span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
